import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet } from './_api';

type FunnelEvent = {
  event: string;
  windowDays: number;
  series: { date: string; count: number }[];
};

type FunnelResponse = {
  windowDays: number;
  events: FunnelEvent[];
};

type TelemetrySummary = {
  windowDays: number;
  totalEvents: number;
  byKind: Array<{ kind: string; count: number }>;
  bySurface: Array<{ surface: string; count: number }>;
  daily: Array<{ day: string; client: number; server: number }>;
  formCompletion: Array<{
    formId: string;
    started: number;
    submitted: number;
    submitFailed: number;
    abandoned: number;
    completionPct: number;
    medianTimeToSubmitMs: number | null;
  }>;
  errors: {
    api: number;
    client: number;
    boundary: number;
    topApiEndpoints: Array<{ endpoint: string; count: number }>;
    topMessages: Array<{ message: string; count: number }>;
  };
  filterFriction: Array<{
    filterKey: string;
    applied: number;
    zeroResults: number;
    frictionPct: number;
  }>;
};

const HUMAN_LABEL: Record<string, string> = {
  'account.signed_up': 'Sign-ups',
  'exchange.submitted': 'Requests sent',
  'exchange.confirmed': 'Requests confirmed',
  'exchange.completed': 'Stays completed',
  'event.published': 'Events published',
  'event_interest.submitted': 'Event interest submitted',
  'subscription.trial_ended_first_charge': 'First paid month',
};

export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const funnel = useQuery({
    queryKey: ['admin', 'analytics', 'funnel', 14],
    queryFn: () => adminGet<FunnelResponse>('/api/admin/analytics/funnel?windowDays=14'),
  });

  const summary = useQuery({
    queryKey: ['admin', 'analytics', 'telemetry-summary', 7],
    queryFn: () =>
      adminGet<TelemetrySummary>('/api/admin/analytics/telemetry/summary?windowDays=7'),
  });

  return (
    <AdminShell>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Title order={2}>Analytics</Title>
            <Text c="dimmed" size="sm">
              14-day funnel up top, 7-day client telemetry below.
            </Text>
          </Stack>
          <Button
            component={Link}
            to="/admin/analytics-events"
            variant="light"
            size="sm"
          >
            Open events explorer
          </Button>
        </Group>

        {funnel.isLoading && <Loader />}

        {funnel.isError && (
          <Alert color="red" variant="light">
            Couldn&apos;t load funnel. Try again.
          </Alert>
        )}

        {funnel.data && (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {funnel.data.events.map((e) => (
              <FunnelCard key={e.event} entry={e} />
            ))}
          </SimpleGrid>
        )}

        <Stack gap={4} mt="xl">
          <Title order={3}>Client telemetry · last 7 days</Title>
          <Text c="dimmed" size="sm">
            Authenticated-session events from the SPA, mirrored server emits, and
            error captures. Drill in via the events explorer.
          </Text>
        </Stack>

        {summary.isLoading && <Loader />}
        {summary.isError && (
          <Alert color="red" variant="light">
            Couldn&apos;t load telemetry summary. The TELEMETRY_DB binding may
            not be wired yet. Check wrangler.jsonc.
          </Alert>
        )}

        {summary.data && summary.data.totalEvents === 0 && (
          <Alert color="gray" variant="light">
            No events in the last 7 days. Sign in as a demo user and click
            through a few surfaces to seed the dataset.
          </Alert>
        )}

        {summary.data && summary.data.totalEvents > 0 && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
              <StatBlock label="Total events" value={summary.data.totalEvents} />
              <StatBlock
                label="Errors (API)"
                value={summary.data.errors.api}
                {...(summary.data.errors.api > 0 ? { accent: 'red' as const } : {})}
              />
              <StatBlock
                label="Errors (client)"
                value={summary.data.errors.client + summary.data.errors.boundary}
                {...(summary.data.errors.client + summary.data.errors.boundary > 0
                  ? { accent: 'red' as const }
                  : {})}
              />
              <StatBlock
                label="Distinct kinds"
                value={summary.data.byKind.length}
              />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Card withBorder padding="md" radius="md">
                <Stack gap="sm">
                  <Text fw={500}>Top kinds</Text>
                  {summary.data.byKind.slice(0, 12).map((k) => (
                    <KindBar
                      key={k.kind}
                      label={k.kind}
                      count={k.count}
                      max={summary.data!.byKind[0]?.count ?? 1}
                    />
                  ))}
                </Stack>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Stack gap="sm">
                  <Text fw={500}>Daily volume</Text>
                  <Table
                    striped
                    horizontalSpacing="xs"
                    verticalSpacing={4}
                    style={{ fontSize: 13 }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Day</Table.Th>
                        <Table.Th ta="right">Client</Table.Th>
                        <Table.Th ta="right">Server</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {summary.data.daily.map((d) => (
                        <Table.Tr key={d.day}>
                          <Table.Td>{d.day}</Table.Td>
                          <Table.Td ta="right">{d.client.toLocaleString()}</Table.Td>
                          <Table.Td ta="right">{d.server.toLocaleString()}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>
            </SimpleGrid>

            <Card withBorder padding="md" radius="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={500}>Form completion</Text>
                  <Text size="xs" c="dimmed">
                    submitted ÷ started
                  </Text>
                </Group>
                {summary.data.formCompletion.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No form starts in the window.
                  </Text>
                ) : (
                  <Table striped style={{ fontSize: 13 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Form</Table.Th>
                        <Table.Th ta="right">Started</Table.Th>
                        <Table.Th ta="right">Submitted</Table.Th>
                        <Table.Th ta="right">Failed</Table.Th>
                        <Table.Th ta="right">Abandoned</Table.Th>
                        <Table.Th ta="right">Completion</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {summary.data.formCompletion.map((f) => (
                        <Table.Tr key={f.formId}>
                          <Table.Td>
                            <Text size="sm" ff="monospace">
                              {f.formId}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">{f.started.toLocaleString()}</Table.Td>
                          <Table.Td ta="right">{f.submitted.toLocaleString()}</Table.Td>
                          <Table.Td ta="right">{f.submitFailed.toLocaleString()}</Table.Td>
                          <Table.Td ta="right">{f.abandoned.toLocaleString()}</Table.Td>
                          <Table.Td ta="right">
                            <Badge
                              variant="light"
                              color={f.completionPct >= 60 ? 'tide' : 'yellow'}
                              size="sm"
                            >
                              {f.completionPct}%
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </Card>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Card withBorder padding="md" radius="md">
                <Stack gap="sm">
                  <Text fw={500}>Top failing endpoints</Text>
                  {summary.data.errors.topApiEndpoints.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      Nothing failing. Good.
                    </Text>
                  ) : (
                    <Stack gap={4}>
                      {summary.data.errors.topApiEndpoints.map((e) => (
                        <Group key={e.endpoint} justify="space-between">
                          <Text size="sm" ff="monospace">
                            {e.endpoint}
                          </Text>
                          <Badge variant="light" color="red" size="sm">
                            {e.count}
                          </Badge>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>

              <Card withBorder padding="md" radius="md">
                <Stack gap="sm">
                  <Text fw={500}>Top error messages</Text>
                  {summary.data.errors.topMessages.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No uncaught errors in the window.
                    </Text>
                  ) : (
                    <Stack gap={4}>
                      {summary.data.errors.topMessages.map((m, i) => (
                        <Group key={`${i}-${m.message}`} justify="space-between" wrap="nowrap">
                          <Text size="xs" lineClamp={1} style={{ flex: 1 }}>
                            {m.message}
                          </Text>
                          <Badge variant="light" color="red" size="sm">
                            {m.count}
                          </Badge>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            </SimpleGrid>

            {summary.data.filterFriction.length > 0 && (
              <Card withBorder padding="md" radius="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={500}>Filter friction</Text>
                    <Text size="xs" c="dimmed">
                      zero-result rate by filter
                    </Text>
                  </Group>
                  <Stack gap={4}>
                    {summary.data.filterFriction.map((f) => (
                      <Group key={f.filterKey} justify="space-between" wrap="nowrap">
                        <Text size="sm" ff="monospace">
                          {f.filterKey}
                        </Text>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="xs" c="dimmed">
                            {f.zeroResults}/{f.applied}
                          </Text>
                          <Badge
                            variant="light"
                            color={f.frictionPct >= 40 ? 'red' : f.frictionPct >= 20 ? 'yellow' : 'gray'}
                            size="sm"
                          >
                            {f.frictionPct}%
                          </Badge>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            )}

            <Anchor component={Link} to="/admin/analytics-events" size="sm">
              View raw events →
            </Anchor>
          </>
        )}
      </Stack>
    </AdminShell>
  );
}

function StatBlock({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'red';
}) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap={2}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text
          size="xl"
          fw={600}
          {...(accent === 'red' ? { c: 'red.7' } : {})}
        >
          {value.toLocaleString()}
        </Text>
      </Stack>
    </Card>
  );
}

function KindBar({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <Stack gap={2}>
      <Group justify="space-between" wrap="nowrap">
        <Text size="xs" ff="monospace" lineClamp={1} style={{ flex: 1 }}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {count.toLocaleString()}
        </Text>
      </Group>
      <Progress value={pct} color="brand" size="sm" radius="xs" />
    </Stack>
  );
}

function FunnelCard({ entry }: { entry: FunnelEvent }) {
  const label = HUMAN_LABEL[entry.event] ?? entry.event;
  const total = entry.series.reduce((acc, p) => acc + p.count, 0);
  const recent = entry.series.slice(-7);
  const recentTotal = recent.reduce((acc, p) => acc + p.count, 0);
  const earlier = entry.series.slice(0, Math.max(0, entry.series.length - 7));
  const earlierTotal = earlier.reduce((acc, p) => acc + p.count, 0);
  const trendPct =
    earlierTotal > 0 ? Math.round(((recentTotal - earlierTotal) / earlierTotal) * 100) : null;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={500} lineClamp={1}>
            {label}
          </Text>
          {trendPct !== null && (
            <Badge
              variant="light"
              size="xs"
              color={trendPct >= 0 ? 'tide' : 'yellow'}
            >
              {trendPct >= 0 ? '+' : ''}
              {trendPct}%
            </Badge>
          )}
        </Group>
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Text size="xl" fw={600}>
            {total.toLocaleString()}
          </Text>
          <Sparkline
            points={entry.series.map((p) => p.count)}
            ariaLabel={`${label} daily counts over the last ${entry.windowDays} days`}
          />
        </Group>
        <Text size="xs" c="dimmed">
          {entry.event} · last {entry.windowDays} days
        </Text>
      </Stack>
    </Card>
  );
}

/**
 * Tiny inline sparkline. Pure SVG, no chart deps. Renders an area-fill under a
 * polyline, with a brand-orange stroke. A flat (all-zero) series renders as a
 * faint dimmed baseline so the card still looks intentional rather than
 * broken.
 */
function Sparkline({
  points,
  width = 96,
  height = 28,
  ariaLabel,
}: {
  points: number[];
  width?: number;
  height?: number;
  ariaLabel: string;
}) {
  const maxValue = points.reduce((acc, n) => (n > acc ? n : acc), 0);
  const flat = maxValue === 0;
  const xStep = points.length > 1 ? width / (points.length - 1) : width;

  const coords = points.map((value, i) => {
    const x = points.length > 1 ? i * xStep : width / 2;
    const y = flat ? height - 1 : height - 1 - (value / maxValue) * (height - 2);
    return { x, y };
  });
  const linePath = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(' ');
  const areaPath =
    coords.length > 0
      ? `${linePath} L${coords[coords.length - 1]!.x.toFixed(2)},${height} L${coords[0]!.x.toFixed(2)},${height} Z`
      : '';

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ flexShrink: 0 }}
    >
      {!flat && (
        <path
          d={areaPath}
          fill="var(--mantine-color-brand-1)"
          opacity={0.55}
        />
      )}
      <path
        d={linePath}
        stroke={flat ? 'var(--mantine-color-gray-4)' : 'var(--mantine-color-brand-6)'}
        strokeWidth={flat ? 1 : 1.5}
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
